import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useServerWebSocket } from '../lib/ws.js';
import { api } from '../lib/api.js';
import { ServerState, ConsoleLogMessage } from '@kcdash/shared';
import {
  Terminal,
  Send,
  Trash2,
  Search,
  ArrowDownCircle,
  Pause,
  Play,
  Square,
  RotateCcw,
  RefreshCw,
  Sparkles,
  UserCheck,
  UserX,
  MessageSquare,
  AlertTriangle,
  AlertCircle,
  Cpu,
} from 'lucide-react';

interface ClassifiedLog {
  category: 'CHAT' | 'JOIN' | 'LEAVE' | 'LUA' | 'AGENT' | 'CMD' | 'ERROR' | 'WARN' | 'INFO';
  badgeClass: string;
  rowClass: string;
  textClass: string;
  icon?: React.ReactNode;
}

function classifyLogLine(message: string, level: string): ClassifiedLog {
  const lower = message.toLowerCase();

  // 1. In-game commands
  if (message.startsWith('>') || lower.startsWith('executing command:')) {
    return {
      category: 'CMD',
      badgeClass: 'bg-amber-500/20 text-amber-300 border border-amber-500/40',
      rowClass: 'bg-amber-950/20 border-l-2 border-amber-400',
      textClass: 'text-amber-200 font-semibold',
    };
  }

  // 2. Chat messages
  if (
    lower.includes('[chat]') ||
    lower.includes('chat:') ||
    lower.includes('say:') ||
    lower.startsWith('chat >') ||
    lower.includes('global chat:')
  ) {
    return {
      category: 'CHAT',
      badgeClass: 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40',
      rowClass: 'bg-cyan-950/20 border-l-2 border-cyan-400',
      textClass: 'text-cyan-200 font-medium',
      icon: <MessageSquare className="w-3 h-3 text-cyan-400 shrink-0" />,
    };
  }

  // 3. Player Joined / Connected
  if (
    lower.includes('joined') ||
    lower.includes('connected') ||
    lower.includes('player joined') ||
    lower.includes('authenticated client') ||
    lower.includes('client connected')
  ) {
    return {
      category: 'JOIN',
      badgeClass: 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40',
      rowClass: 'bg-emerald-950/25 border-l-2 border-emerald-400',
      textClass: 'text-emerald-300 font-medium',
      icon: <UserCheck className="w-3 h-3 text-emerald-400 shrink-0" />,
    };
  }

  // 4. Player Left / Disconnected
  if (
    lower.includes('left') ||
    lower.includes('disconnected') ||
    lower.includes('player left') ||
    lower.includes('timed out') ||
    lower.includes('client disconnected') ||
    lower.includes('kicked')
  ) {
    return {
      category: 'LEAVE',
      badgeClass: 'bg-rose-500/20 text-rose-300 border border-rose-500/40',
      rowClass: 'bg-rose-950/25 border-l-2 border-rose-500',
      textClass: 'text-rose-300 font-medium',
      icon: <UserX className="w-3 h-3 text-rose-400 shrink-0" />,
    };
  }

  // 5. Gamemode / Lua
  if (
    lower.includes('[gamemode]') ||
    lower.includes('[lua]') ||
    lower.includes('[script]') ||
    lower.includes('nlua') ||
    lower.includes('gamemodes/')
  ) {
    return {
      category: 'LUA',
      badgeClass: 'bg-purple-500/20 text-purple-300 border border-purple-500/40',
      rowClass: 'bg-purple-950/20 border-l-2 border-purple-400',
      textClass: 'text-purple-200',
      icon: <Sparkles className="w-3 h-3 text-purple-400 shrink-0" />,
    };
  }

  // 6. KCDash Agent / Lifecycle
  if (
    lower.includes('[kcdash agent]') ||
    lower.includes('kcdmp.server') ||
    lower.includes('starting kcdmp') ||
    lower.includes('server verified active')
  ) {
    return {
      category: 'AGENT',
      badgeClass: 'bg-teal-500/20 text-teal-300 border border-teal-500/40',
      rowClass: 'bg-teal-950/20 border-l-2 border-teal-400',
      textClass: 'text-teal-200 font-mono',
      icon: <Cpu className="w-3 h-3 text-teal-400 shrink-0" />,
    };
  }

  // 7. Errors & Exceptions
  if (level === 'error' || lower.includes('error') || lower.includes('exception') || lower.includes('fatal')) {
    return {
      category: 'ERROR',
      badgeClass: 'bg-red-500/20 text-red-300 border border-red-500/40',
      rowClass: 'bg-red-950/30 border-l-2 border-red-500',
      textClass: 'text-red-300 font-mono',
      icon: <AlertCircle className="w-3 h-3 text-red-400 shrink-0" />,
    };
  }

  // 8. Warnings
  if (level === 'warn' || lower.includes('warn') || lower.includes('warning')) {
    return {
      category: 'WARN',
      badgeClass: 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/40',
      rowClass: 'bg-yellow-950/20 border-l-2 border-yellow-400',
      textClass: 'text-amber-200 font-mono',
      icon: <AlertTriangle className="w-3 h-3 text-yellow-400 shrink-0" />,
    };
  }

  // 9. Standard info
  return {
    category: 'INFO',
    badgeClass: 'bg-neutral-800 text-neutral-400 border border-neutral-700',
    rowClass: 'hover:bg-neutral-900/60',
    textClass: 'text-neutral-300',
  };
}

export const LiveConsolePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [commandInput, setCommandInput] = useState('');
  const [filterLevel, setFilterLevel] = useState<'ALL' | 'INFO' | 'WARN' | 'ERROR' | 'CHAT' | 'JOIN'>('ALL');
  const [searchQuery, setSearchQuery] = useState('');
  const [autoScroll, setAutoScroll] = useState(true);
  const [serverState, setServerState] = useState<ServerState>(ServerState.OFFLINE);
  const [isActionPending, setIsActionPending] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  const logsEndRef = useRef<HTMLDivElement>(null);

  // WebSocket connection for instant sub-millisecond updates
  const { logs, isConnected, sendCommand, clearLogs, setLogs } = useServerWebSocket({
    serverId: id,
    onStatusChange: (newState) => {
      setServerState(newState);
    },
  });

  // Load initial logs and server status
  const fetchStatusAndLogs = useCallback(async () => {
    if (!id) return;
    try {
      const server = await api.getServer(id);
      if (server?.state) {
        setServerState(server.state);
      }
      const initialLogs = await api.getLogs(id, 1000);
      setLogs((prev) => {
        const seen = new Set(prev.map((l) => l.id || `${l.timestamp}-${l.message}`));
        const newOnes = initialLogs.filter((l) => !seen.has(l.id || `${l.timestamp}-${l.message}`));
        if (newOnes.length === 0 && prev.length > 0) return prev;
        return [...prev, ...newOnes].slice(-1000);
      });
    } catch {
      // Ignore background fetch error
    }
  }, [id, setLogs]);

  useEffect(() => {
    fetchStatusAndLogs();
  }, [fetchStatusAndLogs]);

  // Live auto-sync fallback: poll every 2.5 seconds so logs appear even if WS experiences a network glitch
  useEffect(() => {
    if (!id) return;
    const interval = setInterval(async () => {
      try {
        const freshLogs = await api.getLogs(id, 200);
        setLogs((prev) => {
          const seen = new Set(prev.map((l) => l.id || `${l.timestamp}-${l.message}`));
          const newOnes = freshLogs.filter((l) => !seen.has(l.id || `${l.timestamp}-${l.message}`));
          if (newOnes.length === 0) return prev;
          return [...prev, ...newOnes].slice(-1000);
        });
      } catch {
        // Silent background fallback
      }
    }, 2500);

    return () => clearInterval(interval);
  }, [id, setLogs]);

  // Scroll to bottom when new logs arrive if auto-scroll is enabled
  useEffect(() => {
    if (autoScroll) {
      logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [logs, autoScroll]);

  const handleSendCommand = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!commandInput.trim()) return;
    sendCommand(commandInput.trim());
    setCommandInput('');
  };

  const handleQuickCommand = (cmd: string) => {
    if (cmd.includes('[')) {
      setCommandInput(cmd);
    } else {
      sendCommand(cmd);
    }
  };

  // Server Lifecycle Actions
  const handleStartServer = async () => {
    if (!id || isActionPending) return;
    setIsActionPending(true);
    setActionError(null);
    try {
      await api.startServer(id);
      setServerState(ServerState.STARTING);
    } catch (err: any) {
      setActionError(err.message || 'Failed to start server');
    } finally {
      setIsActionPending(false);
    }
  };

  const handleStopServer = async () => {
    if (!id || isActionPending) return;
    setIsActionPending(true);
    setActionError(null);
    try {
      await api.stopServer(id);
      setServerState(ServerState.STOPPING);
    } catch (err: any) {
      setActionError(err.message || 'Failed to stop server');
    } finally {
      setIsActionPending(false);
    }
  };

  const handleRestartServer = async () => {
    if (!id || isActionPending) return;
    setIsActionPending(true);
    setActionError(null);
    try {
      await api.restartServer(id);
      setServerState(ServerState.RESTARTING);
    } catch (err: any) {
      setActionError(err.message || 'Failed to restart server');
    } finally {
      setIsActionPending(false);
    }
  };

  const isOnline = serverState === ServerState.ONLINE;
  const isStarting = serverState === ServerState.STARTING || serverState === ServerState.RESTARTING;
  const isStopping = serverState === ServerState.STOPPING;
  const isOffline = serverState === ServerState.OFFLINE || serverState === ServerState.CRASHED;

  const filteredLogs = logs.filter((log) => {
    const classification = classifyLogLine(log.message, log.level);

    // Level/Category filter
    if (filterLevel === 'INFO' && log.level !== 'info') return false;
    if (filterLevel === 'WARN' && log.level !== 'warn') return false;
    if (filterLevel === 'ERROR' && log.level !== 'error') return false;
    if (filterLevel === 'CHAT' && classification.category !== 'CHAT') return false;
    if (filterLevel === 'JOIN' && classification.category !== 'JOIN' && classification.category !== 'LEAVE') return false;

    // Search query
    if (searchQuery.trim()) {
      return log.message.toLowerCase().includes(searchQuery.toLowerCase());
    }

    return true;
  });

  return (
    <div className="flex flex-col h-[calc(100vh-8rem)] bg-panel-card border border-panel-border rounded-xl shadow-lg overflow-hidden">
      {/* Top Console Action Bar */}
      <div className="flex flex-wrap items-center justify-between px-4 py-3 bg-panel-subtle border-b border-panel-border gap-3 text-xs">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 font-mono text-neutral-200 font-semibold">
            <Terminal className="w-4 h-4 text-brand-400" />
            <span>KCD Live Console</span>
          </div>

          {/* Real-time WS connection pill */}
          <span
            className={`px-2.5 py-0.5 rounded-full font-mono text-[10px] uppercase flex items-center gap-1.5 transition-colors ${
              isConnected
                ? 'bg-emerald-950/80 border border-emerald-700/60 text-emerald-400'
                : 'bg-amber-950/80 border border-amber-700/60 text-amber-400'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isConnected ? 'bg-emerald-400 animate-pulse' : 'bg-amber-400'
              }`}
            />
            {isConnected ? 'LIVE WS' : 'POLLING LIVE'}
          </span>

          {/* Current Server State Badge */}
          <span
            className={`px-2.5 py-0.5 rounded-full font-mono text-[10px] uppercase font-bold flex items-center gap-1.5 ${
              isOnline
                ? 'bg-emerald-950 border border-emerald-800 text-emerald-400'
                : isStarting
                ? 'bg-amber-950 border border-amber-800 text-amber-400'
                : isStopping
                ? 'bg-orange-950 border border-orange-800 text-orange-400'
                : 'bg-neutral-900 border border-neutral-700 text-neutral-400'
            }`}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full ${
                isOnline
                  ? 'bg-emerald-400 animate-pulse'
                  : isStarting
                  ? 'bg-amber-400 animate-ping'
                  : 'bg-neutral-500'
              }`}
            />
            {serverState}
          </span>
        </div>

        {/* Server Action Controls: Start, Stop, Restart */}
        <div className="flex items-center gap-2">
          {actionError && (
            <span className="text-red-400 text-[11px] font-mono pr-2">{actionError}</span>
          )}

          <button
            onClick={handleStartServer}
            disabled={isOnline || isStarting || isActionPending}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-xs disabled:opacity-40 disabled:hover:bg-emerald-600 transition shadow-sm"
            title="Start Server"
          >
            <Play className="w-3.5 h-3.5 fill-current" />
            <span>Start</span>
          </button>

          <button
            onClick={handleStopServer}
            disabled={isOffline || isStopping || isActionPending}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600/90 hover:bg-red-500 text-white font-medium text-xs disabled:opacity-40 disabled:hover:bg-red-600 transition shadow-sm"
            title="Stop Server"
          >
            <Square className="w-3.5 h-3.5 fill-current" />
            <span>Stop</span>
          </button>

          <button
            onClick={handleRestartServer}
            disabled={isStarting || isStopping || isActionPending}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-neutral-800 hover:bg-neutral-700 text-neutral-200 border border-neutral-700 font-medium text-xs disabled:opacity-40 transition shadow-sm"
            title="Restart Server"
          >
            <RotateCcw className={`w-3.5 h-3.5 ${isActionPending ? 'animate-spin' : ''}`} />
            <span>Restart</span>
          </button>
        </div>
      </div>

      {/* Filter and Search Sub-header */}
      <div className="flex flex-wrap items-center justify-between px-4 py-2 bg-neutral-950/80 border-b border-panel-border gap-2 text-xs">
        {/* Quick Command Presets */}
        <div className="flex items-center gap-1.5 overflow-x-auto py-0.5">
          <span className="text-neutral-500 text-[11px] font-mono mr-1">Quick:</span>
          {['status', 'say [Announcement]', 'players', 'help'].map((cmd) => (
            <button
              key={cmd}
              onClick={() => handleQuickCommand(cmd)}
              className="px-2 py-0.5 rounded bg-neutral-900 hover:bg-neutral-800 text-neutral-400 hover:text-brand-300 border border-neutral-800 font-mono text-[10px] transition shrink-0"
            >
              {cmd}
            </button>
          ))}
        </div>

        {/* Filters and Controls */}
        <div className="flex items-center gap-2">
          {/* Search Box */}
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="text"
              placeholder="Search logs..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-8 pr-3 py-1 rounded bg-panel-input border border-panel-border text-xs text-neutral-200 placeholder-neutral-500 focus:outline-none focus:border-brand-500 w-36 sm:w-48"
            />
          </div>

          {/* Filter Categories */}
          <select
            value={filterLevel}
            onChange={(e) => setFilterLevel(e.target.value as any)}
            className="px-2.5 py-1 rounded bg-panel-input border border-panel-border text-xs text-neutral-300 font-mono focus:outline-none focus:border-brand-500"
          >
            <option value="ALL">ALL LOGS</option>
            <option value="CHAT">💬 CHAT ONLY</option>
            <option value="JOIN">👥 JOINS / LEAVES</option>
            <option value="INFO">INFO ONLY</option>
            <option value="WARN">⚠️ WARN ONLY</option>
            <option value="ERROR">❌ ERROR ONLY</option>
          </select>

          {/* Autoscroll Toggle */}
          <button
            onClick={() => setAutoScroll(!autoScroll)}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded border text-xs font-mono transition ${
              autoScroll
                ? 'bg-brand-950 border-brand-800 text-brand-400'
                : 'bg-panel-input border-panel-border text-neutral-400 hover:text-neutral-200'
            }`}
            title="Toggle autoscroll"
          >
            {autoScroll ? <ArrowDownCircle className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
            <span className="hidden sm:inline">{autoScroll ? 'AUTO' : 'PAUSED'}</span>
          </button>

          {/* Clear Logs */}
          <button
            onClick={clearLogs}
            className="p-1.5 rounded bg-panel-input border border-panel-border text-neutral-400 hover:text-neutral-200 hover:bg-neutral-800 transition"
            title="Clear view"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>

      {/* Colorful Console Output Area */}
      <div className="flex-1 bg-[#070908] p-3 overflow-y-auto font-mono text-xs space-y-1 console-output selection:bg-brand-800 selection:text-white">
        {filteredLogs.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-neutral-600 gap-2">
            <Terminal className="w-8 h-8 text-neutral-700 animate-pulse" />
            <span>No console output recorded matching current filters.</span>
          </div>
        ) : (
          filteredLogs.map((log) => {
            const classification = classifyLogLine(log.message, log.level);

            return (
              <div
                key={log.id}
                className={`flex items-start gap-2.5 py-1 px-2 rounded transition-colors ${classification.rowClass}`}
              >
                {/* Timestamp */}
                <span className="text-neutral-600 select-none text-[11px] shrink-0 font-mono pt-0.5">
                  {new Date(log.timestamp).toLocaleTimeString()}
                </span>

                {/* Classification Badge with Icon */}
                <span
                  className={`text-[9px] font-bold tracking-wider uppercase shrink-0 px-1.5 py-0.5 rounded select-none flex items-center gap-1 ${classification.badgeClass}`}
                >
                  {classification.icon}
                  {classification.category}
                </span>

                {/* Log message content */}
                <span className={`break-all whitespace-pre-wrap flex-1 pt-0.5 leading-relaxed ${classification.textClass}`}>
                  {log.message}
                </span>
              </div>
            );
          })
        )}
        <div ref={logsEndRef} />
      </div>

      {/* Command Input Bar */}
      <form
        onSubmit={handleSendCommand}
        className="p-3 bg-panel-subtle border-t border-panel-border flex items-center gap-2"
      >
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-panel-input border border-panel-border flex-1 focus-within:border-brand-500 transition shadow-inner">
          <span className="text-brand-400 font-mono font-bold">&gt;</span>
          <input
            type="text"
            placeholder="Enter server command (e.g. status, say Welcome!, kick 1, restart) and press Enter..."
            value={commandInput}
            onChange={(e) => setCommandInput(e.target.value)}
            className="w-full bg-transparent text-sm text-neutral-200 placeholder-neutral-600 font-mono focus:outline-none"
          />
        </div>
        <button
          type="submit"
          disabled={!commandInput.trim()}
          className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-brand-600 hover:bg-brand-500 text-white font-medium text-xs disabled:opacity-40 transition shadow-sm"
        >
          <Send className="w-3.5 h-3.5" />
          <span>Send</span>
        </button>
      </form>
    </div>
  );
};

